// Consent-gated Google Analytics (GA4, measurementId in src/firebase-config.jsx).
// `firebase/analytics` is NEVER imported at module load time — only
// dynamically, and only once the user has consented to the "Statistiche"
// category in the cookie banner (src/lib/consent.js). This keeps the GA
// script/beacon out of the page entirely for users who reject or haven't
// decided yet, per the Garante's "no prior tracking" requirement.
//
// Call `initAnalyticsIfConsented()` on app start (it's a no-op if consent
// isn't analytics:true) and again whenever consent changes to true.
// `disableAnalytics()` turns off collection on an already-created instance
// when consent is withdrawn, and deletes the first-party GA cookies — but a
// full page reload still clears the in-memory instance entirely, so a user
// who revokes and then reloads gets no further GA code at all.

import { getConsent } from './consent';

let analyticsInstance = null;
let initPromise = null;

// Lazily create (or re-enable/return the existing) GA instance. Resolves to
// null if consent isn't given, the firebase app isn't available, or the
// browser doesn't support Analytics (isSupported() — e.g. some
// private-browsing modes). Re-checks consent after every await, so a
// withdrawal that happens mid-init (the race between the dynamic import
// resolving and the user clicking "Rifiuta") still results in no tracking.
export async function initAnalyticsIfConsented() {
  const consent = getConsent();
  if (!consent || consent.analytics !== true) return null;

  if (analyticsInstance) {
    // Re-enable collection in case a previous Reject disabled it in this
    // same session (disableAnalytics() below), without needing a reload.
    try {
      const { setAnalyticsCollectionEnabled } = await import('firebase/analytics');
      if (getConsent()?.analytics !== true) return null; // withdrawn while awaiting
      setAnalyticsCollectionEnabled(analyticsInstance, true);
    } catch (e) {
      /* best-effort re-enable */
    }
    return analyticsInstance;
  }
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      const [{ getAnalytics, isSupported }, { app }] = await Promise.all([
        import('firebase/analytics'),
        import('../firebase-config'),
      ]);
      const supported = await isSupported();
      if (!supported) return null;
      // Consent may have been withdrawn while the imports/isSupported() were
      // in flight — re-check right before actually creating the instance.
      if (getConsent()?.analytics !== true) return null;
      analyticsInstance = getAnalytics(app);
      return analyticsInstance;
    } catch (e) {
      // Blocked script, unsupported environment, etc. — analytics simply
      // stays off; never let this break the app.
      return null;
    } finally {
      initPromise = null;
    }
  })();

  return initPromise;
}

// Turn off collection on an existing instance (consent withdrawn mid-session)
// and delete the first-party GA cookies (`_ga`, `_ga_<container-id>`) so no
// identifier lingers after withdrawal. A subsequent reload drops the
// in-memory instance entirely, so the setAnalyticsCollectionEnabled call is
// only needed to stop collection without forcing a reload.
export async function disableAnalytics() {
  if (analyticsInstance) {
    try {
      const { setAnalyticsCollectionEnabled } = await import('firebase/analytics');
      setAnalyticsCollectionEnabled(analyticsInstance, false);
    } catch (e) {
      /* nothing to disable / module unavailable */
    }
  }
  deleteGaCookies();
}

// GA4's own cookies are always first-party (`_ga`, `_ga_<measurement-id
// suffix>`) and never httpOnly, so they're deletable from document.cookie.
// Clears them on the current host and, defensively, on the parent domain
// (GA sets cookies at the registrable-domain level by default).
function deleteGaCookies() {
  try {
    const existing = document.cookie.split(';').map((c) => c.trim().split('=')[0]).filter(Boolean);
    const gaNames = existing.filter((name) => name === '_ga' || name.startsWith('_ga_'));
    if (gaNames.length === 0) return;

    const host = window.location.hostname;
    const parentDomain = host.split('.').slice(-2).join('.');
    const expired = 'Thu, 01 Jan 1970 00:00:00 GMT';

    gaNames.forEach((name) => {
      document.cookie = `${name}=; expires=${expired}; path=/`;
      document.cookie = `${name}=; expires=${expired}; path=/; domain=${host}`;
      if (parentDomain && parentDomain !== host) {
        document.cookie = `${name}=; expires=${expired}; path=/; domain=.${parentDomain}`;
      }
    });
  } catch (e) {
    /* no document.cookie access — nothing to clean up */
  }
}

// Test-only: reset the module-level singleton between tests.
export function __resetAnalyticsForTests() {
  analyticsInstance = null;
  initPromise = null;
}
